import { createPortal } from "react-dom";
import { apiFetch } from "../utils/apiFetch";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import WorkspaceListShimmer from "./WorkspaceListShimmer";
import { FaSpinner } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import AppNavigation from "./AppNavigation";
import { FiImage, FiX, FiSearch } from "react-icons/fi";
import { useAccountQuery } from "../utils/queries";
import { useQueryClient } from "@tanstack/react-query";
import "./GroupDirectory.css";
import CommunityCard from "./CommunityCard";

const WorkSpacesList = ({ popupOnly = false, embedded = false, directoryTab = false, visible = true, search: externalSearch = "" }) => {
  const [workSpaceName, setWorkSpaceName] = useState("");
  const [localSearch, setGroupSearch] = useState("");
  const groupSearch = directoryTab ? externalSearch : localSearch;
  const navigate = useNavigate();
  const { userId, userInfo } = useAuth();
  const client = useQueryClient();
  const groupsQuery = useAccountQuery(["groups"], "/workspaces");
  const workspaces = groupsQuery.data?.workspaces;
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("academics");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [uploadedThumbnail, setUploadedThumbnail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creationError, setCreationError] = useState("");
  useEffect(() => {
    if (!thumbnailFile) { setThumbnailPreview(""); return; }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);
  const chooseThumbnail = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setCreationError("Choose a JPG, PNG, or WebP image up to 5 MB.");
      return;
    }
    setCreationError(""); setUploadedThumbnail(null); setThumbnailFile(file);
  };
  const [newWorkspacePopup, setNewWorkspacePopup] = useState(false);
  const [courseCreationSpinner, setCourseCreationSpinner] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!popupOnly || params.get("create") !== "1") return;
    setCourseCreationSpinner(true);
    setParams(previous => { const next = new URLSearchParams(previous); next.delete("create"); return next; }, { replace: true });
  }, [params, setParams, popupOnly]);
  useEffect(() => {
    if (!popupOnly) return;
    const open = () => setNewWorkspacePopup(true);
    window.addEventListener("community:create", open);
    return () => window.removeEventListener("community:create", open);
  }, [popupOnly]);
  // const [canCreateCourse, setCanCreateCourse] = useState(false);
  const canCreateCourse = Boolean(userId);

  useEffect(() => {
    if (!courseCreationSpinner || canCreateCourse === null) return;

    setCourseCreationSpinner(false);
    if (canCreateCourse) {
      setNewWorkspacePopup(true);
    } else {
      navigate("/choose-plan");
    }
  }, [courseCreationSpinner, canCreateCourse, navigate]);


  const createWorkSpace = async () => {
    if (creating) return;
    if (canCreateCourse === false) {
      navigate(`/choose-plan`);
      return;
    }
    if (canCreateCourse === null) {
      setCourseCreationSpinner(true);
      return;
    }

    if (workSpaceName.trim() === "") {
      setCreationError("Channel name is required.");
      return;
    }
    if (!thumbnailFile && !uploadedThumbnail) {
      setCreationError("A channel thumbnail is required.");
      return;
    }

    setCreating(true);
    setCreationError("");
    try {
      let upload = uploadedThumbnail;
      if (thumbnailFile && !upload) {
        const response = await apiFetch(`${process.env.API_URL}/api/groups/thumbnail`, {
          method: "POST", headers: { "Content-Type": thumbnailFile.type }, body: thumbnailFile,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not upload thumbnail");
        upload = data;
        setUploadedThumbnail(data);
      }
      const res = await apiFetch(`${process.env.API_URL}/api/workspaceCreate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body: JSON.stringify({
          workSpaceName,
          description,
          category,
          thumbnailToken: upload?.thumbnailToken,
          pricing: { access: "free", currency: "INR", amount: "" },
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        if (res.status === 400) setUploadedThumbnail(null);
        throw new Error(data.message || "Could not create channel");
      }

      const data = await res.json();
      client.invalidateQueries({ queryKey: ["account", userId, "groups"] });
      navigate(`/courses/${data.slug}`);
    } catch (err) {
      setCreationError(err.message);
    } finally {
      setCreating(false);
    }
  };



  const categoryOptions = [
    { value: "academics", label: "Academics" },
    { value: "music", label: "Music" },
    { value: "fitness", label: "Fitness" },
    { value: "technology", label: "Technology" },
    { value: "business", label: "Business" },
    { value: "arts", label: "Arts" },
    { value: "language", label: "Language" },
    { value: "lifestyle", label: "Lifestyle" },
    { value: "other", label: "Other" },
  ];

  return (
    <>
      <div hidden={popupOnly} className={embedded ? "" : "community-directory-page min-h-screen bg-white text-gray-950"}>
        {!embedded && <AppNavigation />}
        {(directoryTab || !Array.isArray(workspaces) || workspaces.length > 0 || groupsQuery.error) && <section hidden={!visible} aria-label="My channels" className={embedded ? "my-communities-section" : "group-directory mx-auto max-w-[1720px] px-4 py-8 sm:px-6"}>
          {!directoryTab && <div className="directory-toolbar">
            <h1 className="text-base font-semibold">My Channels</h1>
              <label className="flex h-11 w-full items-center gap-3 rounded-md border border-gray-300 px-3 sm:w-80"><FiSearch aria-hidden="true" className="shrink-0 text-gray-500" /><input aria-label="Search my channels" type="search" value={groupSearch} onChange={event => setGroupSearch(event.target.value)} placeholder="Search my channels" className="min-w-0 flex-1 bg-transparent text-sm outline-none" /></label>
          </div>}
          {courseCreationSpinner && <p role="status" className="mb-4 flex items-center gap-2 text-sm"><FaSpinner className="animate-spin" />Checking subscription</p>}
          {groupsQuery.error && <div role="alert" className="mb-6 text-sm text-red-700">{groupsQuery.error.message}<button onClick={() => groupsQuery.refetch()} className="ml-3 underline">Try again</button></div>}
          <div>
          {!workspaces && !groupsQuery.error ? <WorkspaceListShimmer /> : <div className="community-card-grid" aria-label="My channels">
            {(workspaces || []).filter(group => (group.name || "").toLowerCase().includes(groupSearch.trim().toLowerCase())).map(workspace => <CommunityCard key={workspace._id} group={workspace} joined userId={userId} />)}
          </div>}
          </div>
          {workspaces && !workspaces.some(group => (group.name || "").toLowerCase().includes(groupSearch.trim().toLowerCase())) && <p className="border-y border-gray-100 py-16 text-center text-gray-600">{groupSearch ? "No channels match your search." : "No channels yet."}</p>}
        </section>}
      </div>
      {newWorkspacePopup && createPortal(
        <div
          onClick={() => { if (!creating) setNewWorkspacePopup(false); }}
          className="fixed inset-0 bg-black/80 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-label="Create a New Channel"
            className="bg-white w-[90dvw] max-w-lg max-h-[90dvh] overflow-y-auto p-6 gap-4 grid rounded-lg"
          >
            <div className="flex flex-col gap-2 text-center sm:text-left">
              <h2 className="text-lg leading-none font-semibold">
                Create a New Channel
              </h2>
              <p className="text-muted-foreground text-[#717182] text-sm">
                Create a home for your content and keep your audience updated.
              </p>
            </div>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <label htmlFor="group-thumbnail" className="block text-sm font-medium">Thumbnail <span className="font-normal text-gray-500">(required)</span></label>
                {thumbnailPreview ? <div className="relative">
                  <img src={thumbnailPreview} alt="Channel thumbnail preview" className="aspect-video w-full rounded-md bg-gray-50 object-contain" />
                  <button type="button" disabled={creating} title="Remove thumbnail" aria-label="Remove thumbnail" onClick={() => { setThumbnailFile(null); setUploadedThumbnail(null); }} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-gray-100 bg-white text-gray-700"><FiX /></button>
                </div> : <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50"><FiImage aria-hidden="true" className="h-7 w-7 text-gray-400" /></div>}
                <input id="group-thumbnail" type="file" accept="image/jpeg,image/png,image/webp" disabled={creating} onChange={chooseThumbnail} className="block w-full min-w-0 text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-orange-50 file:px-3 file:py-2 file:text-orange-800" />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-name"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Channel Name
                </label>
                <input
                  id="workspace-name"
                  disabled={creating}
                  type="text"
                  placeholder="e.g., Studio Notes"
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive "
                  value={workSpaceName}
                  onChange={(e) => setWorkSpaceName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-description"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Description
                </label>
                <textarea
                  id="workspace-description"
                  disabled={creating}
                  placeholder="What's this channel for?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="resize-none bg-[#f3f3f5] border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-gray-500/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-input-background px-3 py-2 text-base transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-none"
                ></textarea>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-category"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Category
                </label>
                <select
                  id="workspace-category"
                  disabled={creating}
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive "
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {creationError && <p role="alert" className="text-sm text-red-700">{creationError}</p>}
            <div className="flex justify-end gap-3">
              <button disabled={creating} onClick={() => setNewWorkspacePopup(false)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 px-4 py-2 has-[>svg]:px-3 border-gray-300 hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={createWorkSpace}
                disabled={creating} aria-busy={creating}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-black text-primary-foreground hover:bg-black/90 h-9 px-4 py-2 has-[>svg]:px-3 text-white"
              >
                {creating ? <FaSpinner aria-label="Creating channel" className="animate-spin" /> : "Create Channel"}
              </button>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
};

export default WorkSpacesList;
